import React, { useEffect, useMemo, useState } from 'react';
import { Paper } from '@material-ui/core';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import {
  Container,
  FilterGroup,
  useListDateFilter,
  useListSelectFilter,
} from '@michelin/acid-components';
import {
  ChangelogsTable,
  ChangeLogTableConfig,
  MetadataProperty,
} from '@michelin/changelogs-ui';
import { changeLogsEndpoint } from 'Types/APITypes';
import { useParams } from 'react-router-dom';
import useUtils from 'hooks/useUtils';
import { CommercialMechanicalServices, CommercialTireServices } from 'components/CommercialServices/CommercialServicesFields';
import { TABBED_CONTENT_HEIGHT } from 'Utils';
import { PageTitle } from 'Utils/CanvasPageTitle';
import {
  renderContacts,
  renderHoursOfOperations,
  renderMechanicalServices,
  renderTireServices,
  renderValues,
} from './ChangeLogFormat';
import GLOBAL_CONFIG from '../../global-variables';

const changeLogFilters = {
  location_information: 'Location Information',
  services: 'Services',
  contacts: 'Contacts',
  products: 'Products',
  ers: 'ERS Information',
};

const changeLogFiltersAll = {
  bill_to_location: 'Location Information',
  bill_to_services: 'Services',
  bill_to_contacts: 'Contacts',
  bill_to_products: 'Products',
  bill_to_ers: 'ERS Information',
};

export const fieldsToIgnore = [
  'gsi1_hash_key',
  'gsi1_range_key',
  'gsi2_hash_key',
  'gsi2_range_key',
  'gsi3_hash_key',
  'gsi3_range_key',
  'gsi4_hash_key',
  'gsi4_range_key',
  'hash_key',
  'range_key',
  'fips_state',
  'fips_county',
  'team_id',
  'extract_date',
  'extract_time',
  'plt_category_name',
  'relationship',
  'changes',
  'current_version',
  'update_user',
  'update_date',
  'create_user',
  'created_date',
  'service_tier_ranking',
  'mcsn',
  'geocode_updated_by_user',
];

function ChangeLog() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { subTitle } = useUtils();
  const { stNumber, btNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const options: { label: string, value: string }[] = [];
  const clonefilters = (stNumber ? changeLogFilters : changeLogFiltersAll);
  const [config, setConfig] = useState<ChangeLogTableConfig>({} as ChangeLogTableConfig);

  const countryCode = permissions.location?.customer_country || 'us';

  const DIFF_CONFIG: MetadataProperty = {
    dealer_profile_ers_services: {
      '*': {
        default: 'YES_NO',
      },
      retail_services: {
        callback: (value: string) => renderValues({ ers_services: value }, countryCode, t),
      },
      ers_tire_services: {
        callback: (value: string) => renderValues(value, countryCode, t),
      },
      tire_services_hours_of_operation: {
        callback: (value: string) => renderHoursOfOperations(value, countryCode, t),
      },
      mechanical_hours_of_operation: {
        callback: (value: string) => renderHoursOfOperations(value, countryCode, t),
      },
      ers_mechanical_services: {
        callback: (value: string) => renderValues(value, countryCode, t),
      },
      rv_motor_coach: {
        callback: (value: string) => renderValues(value, countryCode, t),
      },
    },
    dealer_profile_products: {
      callback: (value: string) => renderValues(value, countryCode, t),
    },
    contacts: {
      callback: (value: any, data: any, field: any) => renderContacts(value, data, field, countryCode, t),
    },
    // Location Information
    customer: {
      callback: (value: any, data: any, field: any) => renderValues(value, countryCode, t, undefined, undefined, data, field),
    },
    dealer_profile_services: {
      network: {
        callback: (value: string) => renderValues(value, countryCode, t),
      },
      mechanical_services: {
        callback: (value: string) => renderMechanicalServices(value as unknown as CommercialMechanicalServices, t),
      },
      tire_services: {
        callback: (value: string) => renderTireServices(value as unknown as CommercialTireServices, t),
      },
      services: {
        callback: (value: string) => renderValues(value, countryCode, t),
      },
      retail_services: {
        callback: (value: string) => renderValues(value, countryCode, t),
      },
      service_tier_ranking: {
        callback: (value: string) => renderValues({ service_tier_ranking: value }, countryCode, t),
      },
    },
  };

  const dictionary: {
    [entity: string]: { [key: string]: string }
  } = {
    dealer_profile_ers_services: {
      ers_tire_services: t('ERS Tire Services'),
      ers_mechanical_services: t('ERS Mechanical Services'),
      rv_motor_coach: t('RV/Motor Coach'),
      tire_services_hours_of_operation: t('Tire Services Hours of Operations'),
      mechanical_hours_of_operation: t('Mechanical Services Hours of Operations'),
      retail_services: t('Services'),
    },
    dealer_profile_services: {
      network: t('Network'),
      mechanical_services: t('Mechanical Services'),
      tire_services: t('Tire Services'),
      retail_services: t('Services'),
      service_tier_ranking: t('Overrides'),
      // services: t('Services'),
    },
  };

  Object.entries(clonefilters).forEach(([key, value]) => {
    options.push({
      label: t(value),
      value: key,
    });
  });

  const sectionFilter = useListSelectFilter({
    label: t('Section'),
    options,
    hashParam: 'entity',
    defaultValue: 'commercial_information',
  });

  const fromFilter = useListDateFilter({
    label: t('From'),
    hashParam: 'from',
    disableFutureDates: true,
  });

  const toFilter = useListDateFilter({
    label: t('To'),
    hashParam: 'to',
    disableFutureDates: true,
  });

  const filterGroup = useMemo(() => new FilterGroup([
    sectionFilter.filter,
    fromFilter.filter,
    toFilter.filter,
  ]), [sectionFilter.filter, fromFilter.filter, toFilter.filter]);

  const metadata = {
    0: {
      '*': {
        callback: (value: string, data: any) => {
          let identifier = value.replace(`${btNumber}_`, '').split('~')[0];
          if (GLOBAL_CONFIG.useMdmIds && data.after.site_id) identifier = data.after.site_id;
          return (<span>{identifier}</span>);
        },
      },
    },
    1: DIFF_CONFIG,
    2: DIFF_CONFIG,
    3: {
      '*': {
        callback: (value: string) => (<span>{value.split('~')[0]}</span>),
      },
    },
  };

  useEffect(() => {
    setConfig({
      metadata,
      columns: [
        {
          caption: t('Identity'),
          field: 'identifier',
          cellType: 'custom',
          width: 'auto',
          visible: (!stNumber),
          allowSearch: true,
        },
        {
          caption: t('Original Value'),
          field: 'origin',
          cellType: 'custom',
          allowSearch: true,
        },
        {
          caption: t('New Value'),
          field: 'diff',
          cellType: 'custom',
          allowSearch: true,
        },
        {
          caption: t('Updated By'),
          field: 'author',
          cellType: 'custom',
          width: 'auto',
          allowSearch: true,
        },
        {
          caption: t('Modify Date'),
          field: 'datetime',
          cellType: 'date',
          alignment: 'right',
          width: 'auto',
        },
      ],
      ignore: fieldsToIgnore,
      filterGroup,
      filters: {
        bill_to_number: btNumber,
        entity: sectionFilter.value,
        from: (fromFilter.value ? `${fromFilter.value.replaceAll('/', '-')}T00:00:00.000Z` : undefined),
        to: (toFilter.value ? `${toFilter.value.replaceAll('/', '-')}T23:59:59.999Z` : undefined),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionFilter.value, fromFilter.value, toFilter.value]);

  return (
    <>
      <PageTitle
        goHomeBtn={!stNumber}
        title={t('Change History')}
        subtitle={subTitle}
      />
      <Container>
        <Paper>
          {useMemo(() => (
            <ChangelogsTable
              key="changelogsTable"
              config={config}
              entity={sectionFilter.value || ''}
              identifier={stNumber || btNumber}
              api={changeLogsEndpoint}
              dictionary={dictionary}
              height={TABBED_CONTENT_HEIGHT}
            />
          ), [btNumber, config, dictionary, sectionFilter.value, stNumber])}

        </Paper>
      </Container>
    </>
  );
}

export default ChangeLog;
